/**
 * Adds variable editing UI to code blocks that contain variables.
 * Allows per-block variable overrides without server calls.
 */

import { BaseEnricher } from './BaseEnricher.js';
import { syntaxHighlighter } from './SyntaxHighlightEnricher.js';
import { lineNumberer } from './LineNumberEnricher.js';
import type { VariableEnricherConfig, EnricherContext, CodeBlockMeta } from './types.js';
import type { VariableSystem } from '../variables/VariableSystem.js';

export class VariableEnricher extends BaseEnricher {
  private variableSystem: VariableSystem | undefined;
  private activeEditor: HTMLElement | null = null;
  private escapeHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(config: VariableEnricherConfig) {
    super({ priority: 90, ...config });
    this.variableSystem = config.variableSystem;
  }

  async process(container: HTMLElement, _context: EnricherContext): Promise<void> {
    if (!this.variableSystem) {
      console.warn('VariableEnricher: No variableSystem provided');
      return;
    }

    const codeBlockMeta = this.variableSystem.codeBlockMeta;
    if (!codeBlockMeta || codeBlockMeta.length === 0) {
      return;
    }

    const preBlocks = container.querySelectorAll('pre');

    // Match rendered blocks to source templates by content
    preBlocks.forEach((pre, idx) => {
      const code = pre.querySelector('code');
      if (!code) return;

      const renderedContent = code.textContent ?? '';

      // Find a matching template by checking if substituting variables produces this content
      const blockMeta = this.findMatchingTemplate(codeBlockMeta, renderedContent);
      if (!blockMeta) return;

      const sourceBlockIndex = blockMeta.index;
      const preEl = pre as HTMLElement;

      // Store template and metadata on the element
      preEl.dataset.blockIndex = String(sourceBlockIndex);
      preEl.dataset.template = blockMeta.template;
      preEl.dataset.variables = JSON.stringify(blockMeta.variables);

      // Wrap in a container if not already wrapped
      let wrapper = preEl.parentElement as HTMLElement;
      if (!wrapper.classList.contains('code-block-wrapper')) {
        wrapper = document.createElement('div');
        wrapper.className = 'code-block-wrapper';
        preEl.parentNode!.insertBefore(wrapper, preEl);
        wrapper.appendChild(preEl);
      }

      // Apply any existing local overrides
      if (this.variableSystem!.hasBlockOverrides(sourceBlockIndex)) {
        wrapper.classList.add('has-local-vars');
        this.updateCodeBlockContent(preEl);
      }

      // Add button to wrapper (skip if already exists)
      if (!wrapper.querySelector('.code-var-btn')) {
        const varBtn = this.createVariableButton(blockMeta.variables.length);
        varBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.showEditor(preEl, wrapper, blockMeta.variables, sourceBlockIndex);
        });
        wrapper.insertBefore(varBtn, preEl);
      }
    });
  }

  /**
   * Create the variable edit button.
   */
  private createVariableButton(_varCount: number): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.className = 'code-var-btn';
    btn.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.44.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>
      </svg>
      Vars
    `;
    return btn;
  }

  /**
   * Show the floating variable editor for a code block.
   * Uses DOM APIs for XSS safety.
   */
  private showEditor(pre: HTMLElement, wrapper: HTMLElement, variables: string[], blockIndex: number): void {
    // Close any existing editor
    this.hideEditor();

    const overrides = this.variableSystem!.getBlockOverrides(blockIndex);

    const editor = document.createElement('div');
    editor.className = 'code-var-editor';
    editor.dataset.blockIndex = String(blockIndex);

    // Header
    const header = document.createElement('div');
    header.className = 'code-var-editor-header';

    const headerSpan = document.createElement('span');
    headerSpan.textContent = 'Block Variables';
    header.appendChild(headerSpan);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'code-var-editor-close';
    closeBtn.title = 'Close';
    closeBtn.textContent = '×';
    header.appendChild(closeBtn);

    editor.appendChild(header);

    // Body with variable rows
    const body = document.createElement('div');
    body.className = 'code-var-editor-body';

    for (const varName of variables) {
      const globalValue = this.variableSystem!.userVariables[varName] || '';
      const currentValue = overrides[varName] !== undefined ? overrides[varName] : globalValue;
      const isOverridden = overrides[varName] !== undefined;

      const row = document.createElement('div');
      row.className = 'code-var-row' + (isOverridden ? ' overridden' : '');

      const label = document.createElement('label');
      label.textContent = varName;
      row.appendChild(label);

      const input = document.createElement('input');
      input.type = 'text';
      input.dataset.var = varName;
      input.value = currentValue;
      input.placeholder = globalValue;
      row.appendChild(input);

      body.appendChild(row);
    }

    editor.appendChild(body);

    // Footer
    const footer = document.createElement('div');
    footer.className = 'code-var-editor-footer';

    const resetBtn = document.createElement('button');
    resetBtn.className = 'code-var-reset';
    resetBtn.textContent = 'Reset to Global';
    footer.appendChild(resetBtn);

    editor.appendChild(footer);

    wrapper.appendChild(editor);
    this.activeEditor = editor;

    // Set up event listeners
    this.setupEditorListeners(editor, blockIndex, pre, wrapper);

    // Focus first input
    const firstInput = editor.querySelector('input') as HTMLInputElement | null;
    if (firstInput) {
      firstInput.focus();
      firstInput.select();
    }
  }

  /**
   * Hide the active variable editor.
   */
  private hideEditor(): void {
    if (this.activeEditor) {
      this.activeEditor.remove();
      this.activeEditor = null;
    }
    if (this.escapeHandler) {
      document.removeEventListener('keydown', this.escapeHandler);
      this.escapeHandler = null;
    }
  }

  /**
   * Set up event listeners for the editor.
   */
  private setupEditorListeners(editor: HTMLElement, blockIndex: number, pre: HTMLElement, wrapper: HTMLElement): void {
    // Close button
    const closeBtn = editor.querySelector('.code-var-editor-close');
    closeBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.hideEditor();
    });

    // Reset button
    const resetBtn = editor.querySelector('.code-var-reset');
    resetBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.variableSystem!.clearBlockOverrides(blockIndex);
      wrapper.classList.remove('has-local-vars');
      this.updateCodeBlockContent(pre);
      this.hideEditor();
    });

    // Variable inputs - real-time update
    editor.querySelectorAll('input[data-var]').forEach(input => {
      const inputEl = input as HTMLInputElement;

      inputEl.addEventListener('input', () => {
        const varName = inputEl.dataset.var!;
        const newValue = inputEl.value;
        const globalValue = this.variableSystem!.userVariables[varName] || '';

        this.variableSystem!.setBlockOverride(blockIndex, varName, newValue);

        // Update row styling
        const row = inputEl.closest('.code-var-row');
        if (newValue === globalValue) {
          row?.classList.remove('overridden');
        } else {
          row?.classList.add('overridden');
        }

        // Update has-local-vars class on wrapper
        if (this.variableSystem!.hasBlockOverrides(blockIndex)) {
          wrapper.classList.add('has-local-vars');
        } else {
          wrapper.classList.remove('has-local-vars');
        }

        this.updateCodeBlockContent(pre);
      });

      // Prevent form submission on Enter
      inputEl.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
        }
      });
    });

    // Escape to close
    this.escapeHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.hideEditor();
      }
    };
    document.addEventListener('keydown', this.escapeHandler);

    // Close when a press STARTS outside the editor (mousedown, not click):
    // a text-selection drag that starts inside an input and releases outside
    // must not dismiss the popup.
    setTimeout(() => {
      const closeOnOutside = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (!editor.contains(target) && !target.closest('.code-var-btn')) {
          this.hideEditor();
          document.removeEventListener('mousedown', closeOnOutside);
        }
      };
      document.addEventListener('mousedown', closeOnOutside);
    }, 100);
  }

  /**
   * Update a code block's content with local variable overrides.
   */
  private updateCodeBlockContent(pre: HTMLElement): void {
    const code = pre.querySelector('code');
    if (!code) return;

    const template = pre.dataset.template;
    if (!template) return;

    const blockIndex = parseInt(pre.dataset.blockIndex ?? '0');

    // Apply variables from template
    const newContent = this.variableSystem!.applyVariables(template, blockIndex);
    code.textContent = newContent;

    // Re-apply syntax highlighting and line numbers
    syntaxHighlighter.rehighlight(code as HTMLElement);
    lineNumberer.refreshLineNumbers(pre);
  }

  /**
   * Find a template that matches the rendered content.
   */
  private findMatchingTemplate(codeBlockMeta: CodeBlockMeta[], renderedContent: string): CodeBlockMeta | null {
    // Normalize whitespace for comparison
    const normalizeContent = (str: string) => str.trim().replace(/\r\n/g, '\n');
    const normalizedRendered = normalizeContent(renderedContent);

    for (const meta of codeBlockMeta) {
      // Apply current variable values to the template
      const substituted = this.variableSystem!.applyVariables(meta.template, null);
      const normalizedSubstituted = normalizeContent(substituted);

      if (normalizedSubstituted === normalizedRendered) {
        return meta;
      }
    }

    return null;
  }

  teardown(): void {
    this.hideEditor();
  }
}
